import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

// Estado inicial
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null
};

// Tipos de acciones
const AUTH_ACTIONS = {
  USER_LOADED: 'USER_LOADED',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_ERROR: 'AUTH_ERROR',
  LOGOUT: 'LOGOUT',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_LOADING: 'SET_LOADING'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.USER_LOADED:
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload,
        error: null
      };
    
    case AUTH_ACTIONS.AUTH_SUCCESS:
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        token: action.payload.token,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    
    case AUTH_ACTIONS.AUTH_ERROR:
    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('token');
      return {
        ...state,
        token: null,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    default:
      return state;
  }
};

// Crear contexto
const AuthContext = createContext();

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

// Configurar axios interceptor
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Cargar datos del usuario
  const loadUser = useCallback(async () => {
    try {
      const res = await axios.get('/api/auth/me');
      dispatch({
        type: AUTH_ACTIONS.USER_LOADED,
        payload: res.data.user
      });
    } catch (error) {
      console.error('Error cargando usuario:', error);
      dispatch({
        type: AUTH_ACTIONS.AUTH_ERROR,
        payload: error.response?.data?.message || 'Error cargando usuario'
      });
    }
  }, []);

  // Cargar usuario al iniciar la aplicación (solo una vez)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
      loadUser();
    } else {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.post('/api/auth/login', { email, password });
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_SUCCESS,
        payload: {
          token: res.data.token,
          user: res.data.user
        }
      });
      
      setAuthToken(res.data.token);
      toast.success(res.data.message || 'Login exitoso');
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error en el login';
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  }, []);

  // Register
  const register = useCallback(async (name, email, password) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const res = await axios.post('/api/auth/register', { name, email, password });
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_SUCCESS,
        payload: {
          token: res.data.token,
          user: res.data.user
        }
      });
      
      setAuthToken(res.data.token);
      toast.success(res.data.message || 'Registro exitoso');
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Error en el registro';
      
      dispatch({
        type: AUTH_ACTIONS.AUTH_ERROR,
        payload: message
      });
      
      toast.error(message);
      return { success: false, message };
    }
  }, []);

  // Registro de socios (auto-registro sin login automático)
  const registerSocio = useCallback(async (cedula, name, email, phone, address) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const res = await axios.post('/api/auth/register-socio', { cedula, name, email, phone, address });
      const message = res.data.message || 'Socio registrado exitosamente';
      toast.success(message);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true, data: res.data };
    } catch (error) {
      // Prefer array of validation errors if present
      const validationErrors = error.response?.data?.errors;
      const message = validationErrors && validationErrors.length > 0
        ? validationErrors.map(v => v.msg).join(', ')
        : error.response?.data?.message || 'Error al registrar socio';

      dispatch({ type: AUTH_ACTIONS.AUTH_ERROR, payload: message });
      toast.error(message);
      return { success: false, message };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    try {
      axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Error en logout:', error);
    }

    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    setAuthToken(null);
    toast.error('Sesión cerrada');
  }, []);

  // Limpiar errores
  const clearErrors = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERRORS });
  }, []);

  // Verificar si el usuario es admin o superior
  const isAdmin = () => {
    return state.user && ['admin', 'super-admin'].includes(state.user.role);
  };

  // Verificar si el usuario es super admin
  const isSuperAdmin = () => {
    return state.user && state.user.role === 'super-admin';
  };

  // Verificar si el usuario es sub-admin
  const isSubAdmin = () => {
    return state.user && state.user.role === 'sub-admin';
  };

  // Verificar si el usuario es socio
  const isSocio = () => {
    return state.user && state.user.role === 'socio';
  };

  // Verificar si tiene un permiso específico
  const hasPermission = (resource, action) => {
    if (!state.user) return false;
    
    // Super admin tiene todos los permisos
    if (state.user.role === 'super-admin') return true;
    
    // Admin tiene permisos completos excepto sistema
    if (state.user.role === 'admin') {
      if (resource === 'system') return false;
      return true;
    }
    
    // Verificar permisos específicos
    return state.user.permissions && 
           state.user.permissions[resource] && 
           state.user.permissions[resource][action];
  };

  // Obtener nombre del rol en español
  const getRoleName = (role) => {
    const roles = {
      'super-admin': 'Super Administrador',
      'admin': 'Administrador',
      'sub-admin': 'Sub-Administrador',
      'secretaria': 'Secretaria',
      'socio': 'Socio'
    };
    return roles[role] || role;
  };

  const value = {
    ...state,
    login,
    register,
    registerSocio,
    logout,
    loadUser,
    clearErrors,
    isAdmin,
    isSuperAdmin,
    isSubAdmin,
    isSocio,
    hasPermission,
    getRoleName
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};